if (!document.querySelector('.location-page .property-gallery')) return;
// --- Gallery class ---
class Gallery {
    // Gallery
    #propertyGallery;
    #propertyName;
    #heroWrapper;
    #thumbGrid;
    #galleryBadge;
    // #galleryShowAll;
    #galleryHero;

    // Gallery images
    #galleryImages;
    #thumbCells = [];

    // Swiper
    #swiper;

    // Lightbox
    #lightbox;
    #lightboxGrid;
    #lightboxClose;
    #lightboxItems = [];
    #lightboxTrigger = null;
    #lightboxReady = false;
    #lightboxActiveIndex = 0;

    // Templates
    #mainGallerySlideTemplate;
    #thumbnailButtonTemplate;
    #lightboxFigureTemplate;

    // State
    #mobileBreakpoint = 800;  // ugly! but need to tie up with css breakpoint

    constructor() {
        if (!this.#initGallery()) return;

        // DOM Elements
        this.#galleryImages = window.GALLERY_IMAGES;
        this.#propertyGallery = document.querySelector('.property-gallery');
        this.#propertyName = this.#propertyGallery.dataset.propertyName;
        this.#heroWrapper = document.querySelector('#galleryHero .swiper-wrapper');
        this.#thumbGrid = document.getElementById('galleryThumbGrid');
        this.#lightboxGrid = document.getElementById('galleryLightboxGrid');
        this.#lightbox = document.getElementById('galleryLightbox');
        this.#galleryBadge = document.getElementById('galleryBadge');
        this.#lightboxClose = document.getElementById('galleryLightboxClose');
        this.#galleryHero = document.getElementById('galleryHero');

        // Templates
        this.#mainGallerySlideTemplate = document.getElementById('templateMainGallerySlide');
        this.#thumbnailButtonTemplate = document.getElementById('templateThumbnailButton');
        this.#lightboxFigureTemplate = document.getElementById('templateLightboxFigure');

        // Build gallery
        this.#buildSlides();
        this.#buildThumbs();
        this.#setThumbCells();
        this.#initSkeletons();
        this.#initHero();
        this.#initEvents();
    }

    // --- Initialisation ---
    #initGallery() {
        if (!Array.isArray(window.GALLERY_IMAGES) || window.GALLERY_IMAGES.length === 0) {
            console.warn('Gallery: no image data found — ensure images is defined before gallery.js loads');
            return false;
        }
        return true;
    }

    // --- Templates ---
    #slideTemplate = () => this.#mainGallerySlideTemplate.content.cloneNode(true);
    #thumbTemplate = () => this.#thumbnailButtonTemplate.content.cloneNode(true);
    #lightboxTemplate = () => this.#lightboxFigureTemplate.content.cloneNode(true);

    // --- Thumbnails ---
    #setThumbCells = () => this.#thumbCells = Array.from(this.#thumbGrid.querySelectorAll('.thumb'));

    // --- Image Loading ---
    #loadImage = (image, imgPlaceholder) => {
        const imageLoaded = () => this.#markImageLoaded(image, imgPlaceholder);
        if (image.complete) return imageLoaded();
        // imageLoaded on both load and error - custom error state for images can/should be added here
        image.addEventListener('load', imageLoaded, { once: true });
        image.addEventListener('error', imageLoaded, { once: true });
    };

    #markImageLoaded = (img, imgPlaceholder) => {
        img.setAttribute('data-loaded', '');
        imgPlaceholder?.remove();
    };

    // --- Build/Initialise Slides ---
    #buildSlides() {
        this.#galleryImages.slice(1).forEach((img, i) => {
            const template = this.#slideTemplate();
            const mainImage = template.querySelector('img.main-image');
            const altText = img.Caption || `${this.#propertyName} - photo ${i + 1}`;
            const baseUrl = img.Url;
            mainImage.setAttribute('data-src', mainImage.getAttribute('data-src').replace('__IMG_URL__', baseUrl));
            mainImage.setAttribute('data-srcset', mainImage.getAttribute('data-srcset').replaceAll('__IMG_URL__', baseUrl));
            mainImage.setAttribute('alt', altText);
            this.#heroWrapper.appendChild(template);
        });
    }

    // --- Gallery Badge ---
    #updateGalleryBadge = i => this.#galleryBadge.textContent = `${i + 1} / ${this.#galleryImages.length}`;

    // --- Hero ---
    #initHero() {
        this.#swiper = new Swiper('#galleryHero', {
            observer: true,
            observeSlideChildren: true,
            keyboard: { enabled: true },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                afterInit: s => this.#setActiveSlide(s, 0),
                slideChange: s => this.#setActiveSlide(s, s.activeIndex),
            },
        });
    }

    #setActiveSlide(swiper, index) {
        this.#updateGalleryBadge(index);
        this.#setActiveThumb(index);
        this.#loadNearbySlides(swiper);
    }

    #loadNearbySlides(swiper) {
        const currentIndex = swiper.activeIndex;
        [-1, 0, 1].forEach(offset => {
            const offsetSlide = swiper.slides[currentIndex + offset];
            if (offsetSlide) this.#loadSlide(offsetSlide);
        });
    }

    #loadSlide(slide) {
        const lazyImage = slide.querySelector('img[data-src]');
        if (!lazyImage) return;
        const imgPlaceholder = slide.querySelector('.gallery-slide-loader');
        lazyImage.src = lazyImage.dataset.src;
        if (lazyImage.dataset.srcset) {
            lazyImage.srcset = lazyImage.dataset.srcset;
            lazyImage.removeAttribute('data-srcset');
        }
        lazyImage.removeAttribute('data-src');
        this.#loadImage(lazyImage, imgPlaceholder);
    }

    // --- Thumbs ---
    #buildThumbs() {
        this.#galleryImages.forEach((img, i) => {
            const template = this.#thumbTemplate();
            const button = template.querySelector('button');
            const imgElement = template.querySelector('img');
            button.dataset.index = i;
            button.setAttribute('aria-label', `View photo ${i + 1} of ${this.#galleryImages.length}`);
            button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
            imgElement.setAttribute('src', imgElement.getAttribute('src').replace('__IMG_URL__', img.Url));
            imgElement.alt = '';
            this.#thumbGrid.appendChild(template);
        });
    }

    #setActiveThumb(i) {
        this.#thumbCells.forEach((thumb, index) => {
            thumb.setAttribute('aria-current', index === i ? 'true' : 'false');
        });
        this.#scrollThumbIntoView(i);
    }

    #scrollThumbIntoView = i => this.#thumbCells[i].scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    // --- Skeletons ---
    #initSkeletons() {
        this.#initHeroSkeleton();
        this.#initThumbSkeletons();
    }

    #initHeroSkeleton() {
        const heroImg = this.#galleryHero.querySelector('.swiper-slide img');
        const removeHeroSkeleton = () => this.#propertyGallery.removeAttribute('data-hero-loading');
        if (!heroImg || heroImg.complete) {
            removeHeroSkeleton();
        } else {
            heroImg.addEventListener('load', removeHeroSkeleton, { once: true });
            heroImg.addEventListener('error', removeHeroSkeleton, { once: true });
        }
    }

    #initThumbSkeletons() {
        const thumbImgs = Array.from(this.#thumbGrid.querySelectorAll('img'));
        const pending = new Set();
        const onThumbSettled = img => {
            pending.delete(img);
            if (pending.size === 0) this.#propertyGallery.removeAttribute('data-thumbs-loading');
        };

        // Only wait for thumbs visible on first paint — off-screen thumbs don't block skeleton removal
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.complete) {
                    const img = entry.target;
                    pending.add(img);
                    img.addEventListener('load', () => onThumbSettled(img), { once: true });
                    img.addEventListener('error', () => onThumbSettled(img), { once: true });
                }
            });
            observer.disconnect();
            if (pending.size === 0) this.#propertyGallery.removeAttribute('data-thumbs-loading');
        }, { root: this.#thumbGrid, threshold: 0 });

        thumbImgs.forEach(img => observer.observe(img));
    }

    // --- Lightbox ---

    // Keep keyboard focus cycling within the lightbox when it's open (accessibility requirement)
    #trapFocus() {
        const focusable = this.#lightbox.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        this.#lightbox.addEventListener('keydown', e => {
            if (e.key !== 'Tab') return;
            const reachedBoundary = e.shiftKey
                ? document.activeElement === first
                : document.activeElement === last;
            if (!reachedBoundary) return;
            e.preventDefault();
            (e.shiftKey ? last : first).focus();
        });
    }

    #applyCaption = (figcaption, caption) => caption ? figcaption.textContent = caption : figcaption.remove();

    #buildLightbox() {
        if (this.#lightboxReady) return;
        this.#galleryImages.forEach((img, i) => this.#buildLightboxFigure(img, i));
        this.#lightboxItems = Array.from(this.#lightboxGrid.querySelectorAll('.item'));
        this.#trapFocus();
        this.#lightboxReady = true;
    }

    #buildLightboxFigure(img, i) {
        const template = this.#lightboxTemplate();
        const image = template.querySelector('img.gallery-lightbox-img');
        const imgPlaceholder = template.querySelector('.gallery-lightbox-loader');
        const figcaption = template.querySelector('figcaption');
        const figure = template.querySelector('figure');
        image.setAttribute('src', image.getAttribute('src').replace('__IMG_URL__', img.Url));
        image.setAttribute('srcset', image.getAttribute('srcset').replaceAll('__IMG_URL__', img.Url));
        image.setAttribute('alt', img.Caption || `${this.#propertyName} - photo ${i + 1}`);
        figure.setAttribute('aria-setsize', this.#galleryImages.length);
        figure.setAttribute('aria-posinset', i + 1);
        this.#applyCaption(figcaption, img.Caption);
        this.#lightboxGrid.appendChild(template);
        this.#loadImage(image, imgPlaceholder);
    }

    #navigateLightbox(direction) {
        const newIndex = this.#lightboxActiveIndex + direction;
        if (newIndex < 0 || newIndex >= this.#galleryImages.length) return;
        this.#lightboxActiveIndex = newIndex;
        this.#lightboxItems.forEach((item, i) => item.setAttribute('aria-current', i === this.#lightboxActiveIndex ? 'true' : 'false'));
        this.#lightboxItems[this.#lightboxActiveIndex].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    #openLightbox(trigger) {
        this.#lightboxTrigger = trigger || document.activeElement;
        this.#buildLightbox();
        this.#lightboxActiveIndex = this.#swiper.activeIndex;

        // Set initial aria-current
        this.#lightboxItems.forEach((item, i) =>
            item.setAttribute('aria-current', i === this.#lightboxActiveIndex ? 'true' : 'false')
        );

        this.#lightbox.setAttribute('data-open', '');
        document.body.style.overflow = 'hidden';

        requestAnimationFrame(() => requestAnimationFrame(() => {
            this.#lightboxClose.focus();
            this.#lightboxItems[this.#lightboxActiveIndex]?.scrollIntoView({ block: 'start' });
        }));
    }

    #closeLightbox() {
        this.#lightbox.removeAttribute('data-open');
        document.body.style.overflow = '';
        if (this.#lightboxTrigger) {
            this.#lightboxTrigger.focus();
            this.#lightboxTrigger = null;
        }
    }

    // --- Events ---
    #initEvents() {
        this.#initThumbEvents();
        this.#initLightboxEvents();

        // just open the lightbox when clciking the gallery
        this.#galleryHero.addEventListener('click', e => {
            if (e.target.closest('.swiper-button-prev') || e.target.closest('.swiper-button-next')) return;
            this.#openLightbox(e.currentTarget);
        });

        this.#initKeyboardEvents();
    }

    #initThumbEvents() {
        const thumbIndex = cell => +cell.dataset.index;
        this.#thumbGrid.addEventListener('mouseenter', e => {
            const cell = e.target.closest('.thumb');
            if (!cell) return;
            const slide = this.#swiper.slides[thumbIndex(cell)];
            if (!slide) return;
            const lazyImage = slide.querySelector('img[data-src]');
            if (lazyImage) lazyImage.src = lazyImage.dataset.src;
        }, true);

        this.#thumbGrid.addEventListener('click', e => {
            const cell = e.target.closest('.thumb');
            if (cell) this.#swiper.slideTo(thumbIndex(cell));
        });
    }

    #initLightboxEvents() {
        // this.#galleryShowAll.addEventListener('click', e => this.#openLightbox(e.currentTarget));
        this.#lightboxClose.addEventListener('click', () => this.#closeLightbox());
        // Close when clicking the backdrop (not the content)
        this.#lightbox.addEventListener('click', e => {
            if (e.target === this.#lightbox) this.#closeLightbox();
        });
    }

    #initMobileTapEvents() {
        this.#galleryHero.addEventListener('click', e => {
            if (window.innerWidth <= this.#mobileBreakpoint
                && !e.target.closest('.swiper-button-prev')
                && !e.target.closest('.swiper-button-next')) {
                this.#openLightbox(e.currentTarget);
            }
        });
    }

    #initKeyboardEvents() {
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape') { this.#closeLightbox(); return; }
            if (!this.#lightbox.hasAttribute('data-open')) return;
            if (e.key === 'ArrowLeft') this.#navigateLightbox(-1);
            if (e.key === 'ArrowRight') this.#navigateLightbox(1);
        });
    }
}

new Gallery();

